import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck, Sparkles, Calendar, FileText, CreditCard, Users, Zap, Lock, Heart, Stethoscope } from "lucide-react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { useOrganizationSchema, useSoftwareApplicationSchema } from "@/components/structured-data";
import { Button } from "@/components/ui/button";
import { useBrand } from "@/hooks/use-brand";
import { HeroDashboardPreview } from "@/components/marketing/HeroDashboardPreview";

export default function Landing() {
  const { t } = useTranslation();
  const brand = useBrand();
  const marqueeNamesRaw = t("landing.marqueeNames", { returnObjects: true });
  const marqueeNames = Array.isArray(marqueeNamesRaw) && marqueeNamesRaw.length ? marqueeNamesRaw : [];
  const daysRaw = t("landing.days", { returnObjects: true });
  const days = Array.isArray(daysRaw) ? daysRaw : [];
  const bentoNamesRaw = t("landing.bentoCalendarNames", { returnObjects: true });
  const bentoNames = Array.isArray(bentoNamesRaw) ? bentoNamesRaw : [];
  const bentoSlots = [2, 7, 9, 13, 18, 21];
  const org = useOrganizationSchema();
  const app = useSoftwareApplicationSchema();
  const jsonLd = { "@context": "https://schema.org", "@graph": [org, app] };

  return (
    <MarketingLayout seoKey="home" jsonLd={jsonLd}>
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-24 lg:pt-44 lg:pb-32" data-testid="landing-hero">
        <div className="absolute inset-0 dotted opacity-40 pointer-events-none" aria-hidden />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[600px] w-[900px] rounded-full bg-secondary blur-3xl opacity-50 pointer-events-none" aria-hidden />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs text-muted-foreground mb-8 fade-up">
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
              {t("landing.heroBadge")}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-medium fade-up" style={{animationDelay:"60ms"}}>
              {t("landing.heroTitle1")}<br />
              <span className="italic text-accent">{t("landing.heroTitle2")}</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl fade-up" style={{animationDelay:"160ms"}}>
              {t("landing.heroSubtitle", { brand: brand.brand })}
            </p>
            <p className="mt-5 text-sm fade-up" style={{animationDelay:"200ms"}}>
              <Link to="/features#vertical-modules" className="text-accent underline-offset-4 hover:underline font-medium">
                {t("landing.modulesDeepLink")}
              </Link>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3 fade-up" style={{animationDelay:"260ms"}}>
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-6 h-11" data-testid="hero-cta-start">
                  {t("common.startFreeTrial")} <ArrowRight strokeWidth={1.5} className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/features">
                <Button variant="ghost" size="lg" className="rounded-full px-6 h-11" data-testid="hero-cta-tour">
                  {t("common.takeTour")}
                </Button>
              </Link>
              <span className="text-xs text-muted-foreground ml-2 inline-flex items-center gap-1.5">
                <ShieldCheck strokeWidth={1.5} className="h-3.5 w-3.5" /> {t("common.hipaaNote")}
              </span>
            </div>
          </div>

          {/* Floating dashboard mockup */}
          <div className="relative mt-20 fade-up" style={{animationDelay:"360ms"}}>
            <div className="absolute -inset-x-10 -inset-y-10 bg-gradient-to-b from-transparent via-secondary/20 to-background pointer-events-none rounded-3xl" />
            <div className="relative rounded-2xl border border-border overflow-hidden shadow-[0_30px_100px_-20px_hsl(var(--primary)/0.18)]">
              <HeroDashboardPreview ariaLabel={t("landing.previewAria")} />
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF MARQUEE */}
      <section className="border-y border-border bg-muted/30 py-6 overflow-hidden" data-testid="social-proof">
        <div className="flex marquee-track gap-16 whitespace-nowrap text-muted-foreground text-sm">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16 shrink-0">
              {marqueeNames.map((b) => (
                <span key={`${i}-${b}`} className="font-display text-xl tracking-tight opacity-70">
                  {b}
                </span>
              ))}
            </div>
          ))}
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground px-6">{t("landing.marqueeDisclaimer")}</p>
      </section>

      {/* BENTO FEATURES */}
      <section className="py-28" data-testid="features-bento">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-4">{t("landing.everything")}</p>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-medium">{t("landing.workspaceTitle")}<br />{t("landing.workspaceTitle2")}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
            <BentoCard className="md:col-span-4 row-span-2" testid="bento-scheduling">
              <Calendar strokeWidth={1.5} className="h-5 w-5 text-accent" />
              <h3 className="font-display text-2xl tracking-tight font-medium mt-4">{t("landing.bentoSchedulingTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-md">{t("landing.bentoSchedulingBody")}</p>
              <div className="mt-8 rounded-xl border border-border bg-background overflow-hidden">
                <div className="grid grid-cols-5 text-xs">
                  {days.map((d) => (
                    <div key={d} className="border-r border-border last:border-r-0 p-3 text-muted-foreground">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-5 gap-px bg-border">
                  {Array.from({length: 25}).map((_,i) => (
                    <div key={i} className="bg-background h-12 relative">
                      {bentoSlots.includes(i) && (
                        <div className="absolute inset-1 rounded-md bg-secondary border border-border/60 text-[10px] px-1.5 py-0.5 truncate">
                          {bentoNames[bentoSlots.indexOf(i)]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </BentoCard>

            <BentoCard className="md:col-span-2" testid="bento-notes">
              <FileText strokeWidth={1.5} className="h-5 w-5 text-accent" />
              <h3 className="font-display text-xl tracking-tight font-medium mt-4">{t("landing.bentoNotesTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t("landing.bentoNotesBody")}</p>
            </BentoCard>

            <BentoCard className="md:col-span-2" testid="bento-billing">
              <CreditCard strokeWidth={1.5} className="h-5 w-5 text-accent" />
              <h3 className="font-display text-xl tracking-tight font-medium mt-4">{t("landing.bentoBillingTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t("landing.bentoBillingBody")}</p>
            </BentoCard>

            <BentoCard className="md:col-span-3" testid="bento-clients">
              <Users strokeWidth={1.5} className="h-5 w-5 text-accent" />
              <h3 className="font-display text-xl tracking-tight font-medium mt-4">{t("landing.bentoClientsTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t("landing.bentoClientsBody")}</p>
            </BentoCard>

            <BentoCard className="md:col-span-3" testid="bento-telehealth">
              <Zap strokeWidth={1.5} className="h-5 w-5 text-accent" />
              <h3 className="font-display text-xl tracking-tight font-medium mt-4">{t("landing.bentoTelehealthTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t("landing.bentoTelehealthBody")}</p>
            </BentoCard>

            <BentoCard className="md:col-span-6" testid="bento-verticals">
              <Stethoscope strokeWidth={1.5} className="h-5 w-5 text-accent" />
              <h3 className="font-display text-xl tracking-tight font-medium mt-4">{t("landing.bentoVerticalsTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-3xl">{t("landing.bentoVerticalsBody")}</p>
            </BentoCard>

            <BentoCard className="md:col-span-2" testid="bento-hipaa">
              <Lock strokeWidth={1.5} className="h-5 w-5 text-accent" />
              <h3 className="font-display text-xl tracking-tight font-medium mt-4">{t("landing.bentoSecurityTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t("landing.bentoSecurityBody")}</p>
            </BentoCard>

            <BentoCard className="md:col-span-2" testid="bento-care">
              <Heart strokeWidth={1.5} className="h-5 w-5 text-accent" />
              <h3 className="font-display text-xl tracking-tight font-medium mt-4">{t("landing.bentoCareTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t("landing.bentoCareBody")}</p>
            </BentoCard>

            <BentoCard className="md:col-span-2" testid="bento-ai">
              <Sparkles strokeWidth={1.5} className="h-5 w-5 text-accent" />
              <h3 className="font-display text-xl tracking-tight font-medium mt-4">{t("landing.bentoCopilotTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t("landing.bentoCopilotBody")}</p>
            </BentoCard>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-24 border-t border-border" id="stories" data-testid="testimonials">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-6">{t("landing.testimonialsEyebrow")}</p>
          <blockquote className="font-display text-3xl md:text-5xl tracking-tighter leading-tight font-medium">
            {t("landing.testimonialQuote", { brand: brand.brand })}
          </blockquote>
          <div className="mt-10 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-secondary grid place-items-center font-display font-medium text-sm">
              {t("landing.testimonialInitials")}
            </div>
            <div>
              {t("landing.testimonialAuthor") ? (
                <p className="font-medium">{t("landing.testimonialAuthor")}</p>
              ) : null}
              <p className="text-sm text-muted-foreground">{t("landing.testimonialRole")}</p>
            </div>
          </div>
          <Link to="/features" className="inline-block mt-6 text-sm text-accent hover:underline">
            {t("landing.moreStories")}
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 border-t border-border" data-testid="bottom-cta">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 text-center">
          <h2 className="font-display text-5xl md:text-6xl tracking-tighter font-medium">{t("landing.ctaTitle")}</h2>
          <p className="mt-5 text-muted-foreground max-w-xl mx-auto">{t("landing.ctaSubtitle")}</p>
          <div className="mt-8 flex items-center gap-3 justify-center">
            <Link to="/signup">
              <Button size="lg" className="rounded-full px-7 h-12" data-testid="footer-cta">
                {t("common.startFreeTrial")} <ArrowRight strokeWidth={1.5} className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="ghost" className="rounded-full px-7 h-12" data-testid="footer-pricing">{t("common.seePricing")}</Button>
            </Link>
          </div>
        </div>
      </section>

    </MarketingLayout>
  );
}

function BentoCard({ children, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`group relative rounded-2xl border border-border bg-card p-7 hover:-translate-y-0.5 transition-transform duration-300 ${className}`}
    >
      {children}
    </div>
  );
}
